import { css } from '@emotion/react';
import styled from '@emotion/styled';

import materialSymbol from 'storybookConfig/mixins/materialSymbol';
import type { ChipProps } from 'storybookConfig/stories/molecules/Chip';
import Chip, { ChipSizes } from 'storybookConfig/stories/molecules/Chip';
import { ThemeColors } from 'storybookConfig/stories/theme';

export enum StatusChipKinds {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  Pending = 'pending',
}

interface StatusChipProps extends Omit<ChipProps, 'variant'> {
  /**
   * The kind of status to display
   */
  kind: `${StatusChipKinds}`;
  /**
   * Whether or not to display an icon.
   */
  hasIcon?: boolean;
}

/**
 * A `StatusChip` is a special type of `Chip` used to indicate statuses.
 * Icons applied are standardized and should not be changed.
 * Supports `small` size but will remove the icon.
 * Will default to `info` if no `kind` is provided.
 */
const StatusChip = styled(Chip)<StatusChipProps>`
  padding: ${({ hasIcon }) => (hasIcon ? '0 8px 0 4px;' : '0 8px;')};
  ${({ theme, kind, hasIcon, size }) => {
    const shouldDisplayIcon = hasIcon && size !== ChipSizes.Small;

    switch (kind) {
      case StatusChipKinds.Success:
        return css`
          background: ${theme.color.successTint};
          border-color: color-mix(in srgb, ${ThemeColors.successStrong} 30%, transparent);
          color: ${theme.color.successStrong};

          ${shouldDisplayIcon &&
          materialSymbol({ name: 'circle-check', color: 'successStrong', size: '20px' })};
        `;
      case StatusChipKinds.Warning:
        return css`
          background: ${theme.color.warningTint};
          border-color: color-mix(in srgb, ${ThemeColors.warningStrong} 30%, transparent);
          color: ${theme.color.warningStrong};

          ${shouldDisplayIcon &&
          materialSymbol({ name: 'triangle-alert', color: 'warningStrong', size: '20px' })};
        `;
      case StatusChipKinds.Error:
        return css`
          background: ${theme.color.errorTint};
          border-color: color-mix(in srgb, ${ThemeColors.errorStrong} 30%, transparent);
          color: ${theme.color.errorStrong};

          ${shouldDisplayIcon &&
          materialSymbol({ name: 'octagon-alert', color: 'errorStrong', size: '20px' })};
        `;
      case StatusChipKinds.Pending:
        return css`
          background: ${theme.color.surfaceSubtle};
          border: 1px dashed color-mix(in srgb, ${ThemeColors.borderMedium} 60%, transparent);
          color: ${theme.color.bodyTextSecondary};

          ${shouldDisplayIcon &&
          materialSymbol({ name: 'clock', color: 'iconDefault', size: '20px' })};
        `;
      case StatusChipKinds.Info:
      default:
        return css`
          background: ${theme.color.infoTint};
          border-color: color-mix(in srgb, ${ThemeColors.accent} 30%, transparent);
          color: ${theme.color.infoStrong};

          ${shouldDisplayIcon &&
          materialSymbol({ name: 'info', color: 'infoStrong', size: '20px' })};
        `;
    }
  }}
`;

export default StatusChip;
